import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Globe, CheckCircle, Shield, Cpu } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import PageSEO from '../components/PageSEO';
import StructuredData from '../components/StructuredData';

const COMPANY_IMAGE = "https://images.pexels.com/photos/3807329/pexels-photo-3807329.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CompanyPage = () => {
  const { t } = useLanguage();

  const commitments = [
    { icon: Shield, title: t('company.commitQuality'), description: t('company.commitQualityDesc') },
    { icon: Target, title: t('company.commitAccuracy'), description: t('company.commitAccuracyDesc') },
    { icon: Cpu, title: t('company.commitTech'), description: t('company.commitTechDesc') },
  ];

  return (
    <div className="relative pt-20" data-testid="company-page">
      <PageSEO path="/company" />
      <StructuredData path="/company" />
      <div className="ambient-glow ambient-orange" />
      <div className="ambient-glow ambient-blue" />
      <div className="fixed top-20 left-0 right-0 glow-line z-40" />

      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] to-[#080810]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-sm font-semibold text-[#00D4FF] tracking-widest uppercase mb-4">{t('company.heroLabel')}</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
              {t('company.heroTitle1')}
              <span className="gradient-text-orange">{t('company.heroTitle2')}</span>
            </h1>
            <p className="text-zinc-400 max-w-3xl mx-auto text-base md:text-lg">{t('company.heroDesc')}</p>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding bg-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-8">
              <div className="glass-card rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-[#FF5E00]/20 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-[#FF5E00]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-white">{t('company.aboutTitle')}</h2>
                </div>
                <p className="text-zinc-400 leading-relaxed mb-4">{t('company.aboutP1')}</p>
                <p className="text-zinc-400 leading-relaxed mb-4">{t('company.aboutP2')}</p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#FF5E00] flex-shrink-0" />
                    <span className="text-white font-medium">{t('company.aboutTeam1')}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#FF5E00] flex-shrink-0" />
                    <span className="text-white font-medium">{t('company.aboutTeam2')}</span>
                  </li>
                </ul>
                <p className="text-zinc-400 leading-relaxed mb-4">{t('company.aboutP3')}</p>
                <p className="text-zinc-400 leading-relaxed mb-4">{t('company.aboutP4')}</p>
                <p className="text-zinc-400 leading-relaxed">{t('company.aboutP5')}</p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative lg:sticky lg:top-28">
              <div className="relative rounded-2xl overflow-hidden">
                <img src={COMPANY_IMAGE} alt="Automotive Professional" className="w-full h-[500px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#05050A]/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="glass-card rounded-xl p-6">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="font-heading text-2xl md:text-3xl font-bold text-[#FF5E00]">100+</p>
                        <p className="text-zinc-500 text-xs md:text-sm">{t('company.statsServices')}</p>
                      </div>
                      <div>
                        <p className="font-heading text-2xl md:text-3xl font-bold text-[#00D4FF]">{t('company.statsGlobal')}</p>
                        <p className="text-zinc-500 text-xs md:text-sm">{t('company.statsCoverage')}</p>
                      </div>
                      <div>
                        <p className="font-heading text-2xl md:text-3xl font-bold text-[#FF5E00]">24/7</p>
                        <p className="text-zinc-500 text-xs md:text-sm">{t('company.statsSupport')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-[#FF5E00]/30 rounded-2xl" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-[#00D4FF]/30 rounded-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="section-padding bg-gradient-to-b from-[#05050A] via-[#080810] to-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <p className="text-sm font-semibold text-[#FF5E00] tracking-widest uppercase mb-4">{t('company.commitLabel')}</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">{t('company.commitTitle')}</h2>
            <p className="text-zinc-400 max-w-3xl mx-auto text-base md:text-lg">{t('company.commitDesc')}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl p-8 mb-12">
            <p className="text-zinc-300 text-center text-lg">{t('company.commitTrust')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {commitments.map((item, index) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="glass-card rounded-2xl p-8 text-center">
                <div className="w-16 h-16 mx-auto rounded-xl bg-[#FF5E00]/20 flex items-center justify-center mb-6">
                  <item.icon className="w-8 h-8 text-[#FF5E00]" />
                </div>
                <h3 className="font-heading font-bold text-white text-xl mb-3">{item.title}</h3>
                <p className="text-zinc-400">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl p-8 text-center">
            <p className="text-zinc-400 text-lg leading-relaxed">
              {t('company.commitBottom')} <span className="text-white font-semibold">{t('company.commitSpeed')}</span>, <span className="text-white font-semibold">{t('company.commitReliability')}</span> {t('company.commitExcellence') !== t('company.commitExcellence') ? '' : ','} <span className="text-white font-semibold">{t('company.commitExcellence')}</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#05050A]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card rounded-2xl p-8 md:p-12 text-center">
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">{t('company.ctaTitle')}</h3>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">{t('company.ctaDesc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" className="glow-button px-8 py-4 text-base">{t('company.ctaBtn')}</Link>
              <Link to="/services" className="secondary-button px-8 py-4 text-base">{t('company.ctaBtn2')}</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CompanyPage;
